// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Badge, Button, Group, Modal, Pill, Stack, Text } from '@mantine/core';
import type { Patient } from '@medplum/fhirtypes';
import { ResourceInput } from '@medplum/react';
import { type JSX, useCallback, useState } from 'react';
import { useGrantSwePatients } from './hooks/useGrantSwePatients';

interface SelectedPatient {
  id: string;
  label: string;
  alreadyGranted: boolean;
}

interface GrantPatientsModalProps {
  sweId: string;
  sweName: string;
  currentPatientIds: string[];
  onGranted: () => void;
  onClose: () => void;
}

export function GrantPatientsModal({
  sweId,
  sweName,
  currentPatientIds,
  onGranted,
  onClose,
}: GrantPatientsModalProps): JSX.Element {
  const [selected, setSelected] = useState<SelectedPatient[]>([]);
  const [inputKey, setInputKey] = useState(0);

  const handleSuccess = useCallback(() => {
    onGranted();
    onClose();
  }, [onGranted, onClose]);

  const { grant, loading, error } = useGrantSwePatients(sweId, handleSuccess);

  const handlePatientChange = useCallback(
    (patient: Patient | undefined): void => {
      if (!patient?.id) {
        return;
      }
      const alreadySelected = selected.some((s) => s.id === patient.id);
      if (!alreadySelected) {
        const given = patient.name?.[0]?.given?.join(' ') ?? '';
        const family = patient.name?.[0]?.family ?? '';
        const label = [given, family].filter(Boolean).join(' ') || 'Unknown';
        setSelected((prev) => [
          ...prev,
          { id: patient.id as string, label, alreadyGranted: currentPatientIds.includes(patient.id as string) },
        ]);
      }
      setInputKey((k) => k + 1);
    },
    [selected, currentPatientIds]
  );

  const handleRemove = (id: string): void => {
    setSelected((prev) => prev.filter((s) => s.id !== id));
  };

  const handleConfirm = (): void => {
    grant(selected.map((s) => s.id)).catch(console.error);
  };

  const handleClose = (): void => {
    setSelected([]);
    onClose();
  };

  return (
    <Modal
      opened
      onClose={handleClose}
      title={`Grant patients to ${sweName}`}
      size="lg"
      data-testid="grant-patients-modal"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Search for patients by name. Select one or more and click "Grant access".
        </Text>

        <ResourceInput<Patient>
          key={inputKey}
          resourceType="Patient"
          name="patient"
          label="Search patients"
          placeholder="Type a name to search..."
          onChange={handlePatientChange}
        />

        {selected.length > 0 && (
          <Pill.Group data-testid="selected-patients">
            {selected.map((s) => (
              <Pill key={s.id} withRemoveButton onRemove={() => handleRemove(s.id)}>
                <Group gap={4} wrap="nowrap" component="span">
                  <span>{s.label}</span>
                  {s.alreadyGranted && (
                    <Badge size="xs" variant="light" color="blue" data-testid={`already-granted-badge-${s.id}`}>
                      Already granted
                    </Badge>
                  )}
                </Group>
              </Pill>
            ))}
          </Pill.Group>
        )}

        {error && (
          <Text size="sm" c="red" data-testid="grant-error-message">
            {error}
          </Text>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={handleClose} disabled={loading} data-testid="grant-cancel-button">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            loading={loading}
            disabled={selected.length === 0}
            data-testid="grant-confirm-button"
          >
            Grant access
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
